<!-- eslint-disable -->
<template>
  <div class="chat-widget">
    <!-- Open chat windows -->
    <chat-window
      v-for="(chat, index) in openChats"
      :key="chat.conversationId"
      :conversation="chat"
      :position="getWindowPosition(index)"
      :minimized="chat.minimized"
      @close="closeChat(chat.conversationId)"
      @minimize="toggleMinimize(chat.conversationId)"
      @toggle-minimize="toggleMinimize(chat.conversationId)"
      @preview-image="previewImage"
      @update-unread="updateUnreadCount"
      @new-message="onNewMessage"
      @online-status-change="onOnlineStatusChange"
    />

    <!-- Chat list popup -->
    <div v-if="showChatList" class="chat-list-popup">
      <div class="chat-list-header">
        <span>{{ $t('chat.title') }}</span>
        <v-btn icon size="x-small" variant="text" @click="showChatList = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      
      <div class="chat-list-search">
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('chat.searchConversations')"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
        ></v-text-field>
      </div>

      <div class="chat-list-content">
        <div v-if="loadingConversations" class="text-center py-4">
          <v-progress-circular indeterminate size="24"></v-progress-circular>
        </div>
        
        <div v-else-if="filteredConversations.length === 0" class="text-center py-4 text-grey">
          {{ $t('chat.noConversations') }}
        </div>

        <div
          v-for="conv in filteredConversations"
          :key="conv.conversationId"
          class="chat-list-item"
          @click="openChat(conv)"
        >
          <div class="position-relative">
            <v-avatar size="40" color="primary">
              <v-img v-if="conv.otherUser.avatarUrl" :src="conv.otherUser.avatarUrl"></v-img>
              <span v-else>{{ conv.otherUser.displayName?.charAt(0) || '?' }}</span>
            </v-avatar>
            <span 
              v-if="conv.otherUser.onlineStatus !== 'hidden'"
              class="online-dot"
              :class="conv.otherUser.onlineStatus"
            ></span>
          </div>
          
          <div class="chat-list-item-info">
            <div class="chat-list-item-name">{{ conv.otherUser.displayName }}</div>
            <div class="chat-list-item-preview">
              <template v-if="conv.lastMessage">
                <span v-if="conv.lastMessage.senderId === currentUserId">{{ $t('chat.you') }}: </span>
                {{ conv.lastMessage.preview || $t('chat.image') }}
              </template>
              <span v-else class="text-grey">{{ $t('chat.noMessages') }}</span>
            </div>
          </div>
          
          <div class="chat-list-item-meta">
            <div class="chat-list-item-time">{{ formatTime(conv.lastMessageAt) }}</div>
            <v-badge
              v-if="conv.unreadCount > 0"
              :content="conv.unreadCount"
              color="error"
              inline
            ></v-badge>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating bubble -->
    <div class="chat-bubble" @click="toggleChatList">
      <v-icon size="28" color="white">mdi-chat</v-icon>
      <span v-if="totalUnread > 0" class="unread-badge">{{ totalUnread > 99 ? '99+' : totalUnread }}</span>
    </div>

    <!-- Image preview dialog -->
    <v-dialog v-model="showImagePreview" max-width="90vw">
      <v-img :src="previewImageUrl" max-height="90vh" contain @click="showImagePreview = false"></v-img>
    </v-dialog>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted, onUnmounted, provide } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import ChatWindow from './ChatWindow.vue'

const store = useStore()
const { t } = useI18n()

const conversations = ref([])
const loadingConversations = ref(true)
const showChatList = ref(false)
const searchQuery = ref('')
const openChats = ref([]) // Array of open chat conversations
const totalUnread = ref(0)

const showImagePreview = ref(false)
const previewImageUrl = ref('')

let refreshInterval = null

const currentUserId = computed(() => store.getters['auth/user']?.userId)
const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])

const filteredConversations = computed(() => {
  if (!searchQuery.value) return conversations.value
  const q = searchQuery.value.toLowerCase()
  return conversations.value.filter(c =>
    c.otherUser.displayName?.toLowerCase().includes(q) ||
    c.otherUser.username?.toLowerCase().includes(q)
  )
})

const formatTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
  if (diffDays === 1) return t('chat.yesterday')
  if (diffDays < 7) return date.toLocaleDateString('da-DK', { weekday: 'short' })
  return date.toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit' })
}

const getWindowPosition = (index) => {
  // Stack windows from right, 340px apart (320px width + 20px gap)
  return 20 + (index * 340)
}

const loadConversations = async () => {
  if (!isAuthenticated.value) return
  
  loadingConversations.value = true
  try {
    const response = await api.get('/conversations')
    conversations.value = response.data.conversations || []
    totalUnread.value = response.data.totalUnread || 0
    
    // Update online status for open chats
    for (const openChat of openChats.value) {
      const updated = conversations.value.find(c => c.conversationId === openChat.conversationId)
      if (updated) {
        openChat.otherUser.onlineStatus = updated.otherUser.onlineStatus
      }
    }
  } catch (error) {
    console.error('Error loading conversations:', error)
  } finally {
    loadingConversations.value = false
  }
}

const toggleChatList = () => {
  showChatList.value = !showChatList.value
  if (showChatList.value) {
    loadConversations()
  }
}

const openChat = (conv) => {
  // Check if already open
  const existing = openChats.value.find(c => c.conversationId === conv.conversationId)
  if (existing) {
    existing.minimized = false
    return
  }
  
  // Limit to 3 open chats
  if (openChats.value.length >= 3) {
    openChats.value.shift() // Remove oldest
  }
  
  openChats.value.push({
    ...conv,
    minimized: false
  })
  
  showChatList.value = false
}

const closeChat = (conversationId) => {
  openChats.value = openChats.value.filter(c => c.conversationId !== conversationId)
}

const toggleMinimize = (conversationId) => {
  const chat = openChats.value.find(c => c.conversationId === conversationId)
  if (chat) {
    chat.minimized = !chat.minimized
  }
}

const updateUnreadCount = (conversationId, count) => {
  const conv = conversations.value.find(c => c.conversationId === conversationId)
  if (conv) {
    conv.unreadCount = count
  }
  // Recalculate total
  totalUnread.value = conversations.value.reduce((sum, c) => sum + (c.unreadCount || 0), 0)
}

const onNewMessage = (conversationId) => {
  // Refresh conversation list to update last message preview
  loadConversations()
}

const onOnlineStatusChange = (conversationId, newStatus) => {
  // Update in conversations list
  const conv = conversations.value.find(c => c.conversationId === conversationId)
  if (conv) {
    conv.otherUser.onlineStatus = newStatus
  }
  // Update in open chats
  const openChat = openChats.value.find(c => c.conversationId === conversationId)
  if (openChat) {
    openChat.otherUser.onlineStatus = newStatus
  }
}

const previewImage = (url) => {
  previewImageUrl.value = url
  showImagePreview.value = true
}

// Start chat from profile page
const startChatWithUser = async (userId, userData) => {
  try {
    // Create or get existing conversation
    const response = await api.post('/conversations', { userId })
    const conversationId = response.data.conversationId
    
    // Check if already in conversations list
    let conv = conversations.value.find(c => c.conversationId === conversationId)
    
    if (!conv) {
      // Create a temporary conversation object
      conv = {
        conversationId,
        otherUser: {
          userId: userData.userId,
          username: userData.username,
          displayName: userData.displayName || userData.username,
          avatarUrl: userData.avatarUrl,
          onlineStatus: userData.onlineStatus || 'offline'
        },
        lastMessage: null,
        unreadCount: 0,
        lastMessageAt: null
      }
      conversations.value.unshift(conv)
    }
    
    openChat(conv)
  } catch (error) {
    console.error('Error starting chat:', error)
  }
}

// Provide function to child components and other parts of the app
provide('startChatWithUser', startChatWithUser)

// Expose to parent
defineExpose({ startChatWithUser, loadConversations })

onMounted(() => {
  if (isAuthenticated.value) {
    loadConversations()
    // Refresh conversations every 5 seconds for live updates
    refreshInterval = setInterval(loadConversations, 5000)
  }
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>

<style scoped>
.chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 999;
}

.chat-bubble {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4A8B7F 0%, #3d7269 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(74, 139, 127, 0.4);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.chat-bubble:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 25px rgba(74, 139, 127, 0.5);
}

.unread-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #f44336;
  color: white;
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
}

.chat-list-popup {
  position: absolute;
  bottom: 70px;
  right: 0;
  width: 320px;
  max-height: 450px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #4A8B7F;
  color: white;
  font-weight: 500;
}

.chat-list-header .v-btn {
  color: white !important;
}

.chat-list-search {
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.chat-list-content {
  flex: 1;
  overflow-y: auto;
  max-height: 320px;
}

.chat-list-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.chat-list-item:hover {
  background: #f5f5f5;
}

.chat-list-item-info {
  flex: 1;
  min-width: 0;
  margin-left: 12px;
}

.chat-list-item-name {
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-list-item-preview {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-list-item-meta {
  text-align: right;
  margin-left: 8px;
}

.chat-list-item-time {
  font-size: 11px;
  color: #888;
  margin-bottom: 4px;
}

.position-relative {
  position: relative;
}

.online-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
}

.online-dot.online { background: #4CAF50; }
.online-dot.away { background: #FFC107; }
.online-dot.offline { background: #9E9E9E; }
</style>